import * as z from 'zod';
import { auditStampSchema, emailSchema, isoDateTimeSchema, localeSchema, strict, uuidSchema, versionSchema, } from './common.js';
/**
 * Operators, analysts, administrators and the one super administrator (AD-26).
 *
 * **An analyst reads and does not act.** It sees the register in full, the dashboard, the audit
 * trail and the incidents, and the only things it may change are its own account and whether an
 * alert has been seen. What that means verb by verb is `permissions.ts`'s grant table, which is the
 * only place a role's reach is written down; nothing here or in a handler restates it.
 *
 * Users are disabled, never deleted: `audit_log.actor_id` and `presence_events.operator_id` are
 * real foreign keys, so a hard delete either cascades and destroys the trail or blocks and the
 * feature does not work. The schemas below therefore have a `status` and no delete request.
 *
 * The super administrator is seeded from the environment on first boot and is protected by a
 * database trigger against deletion, demotion and disabling. Nothing here can express those three
 * operations against that account; the trigger says so again for anything that bypasses this.
 */
/**
 * Every role, in the order the database enum declares them.
 *
 * **`analyst` is LAST because PostgreSQL appends.** `user_role` is a real enum and a label is only
 * ever added to it with `ALTER TYPE ... ADD VALUE`, which puts it at the end, so this list is kept
 * in the database's own order (`schema.db.test.ts` compares the two member sets). Where an order a
 * person reads matters, it is `ROLE_RANK`'s, never this one.
 */
export const roleSchema = z.enum(['operator', 'admin', 'super_admin', 'analyst']);
/**
 * Ordered from least to most privileged, for a comparison that is not a chain of equality tests.
 *
 * It decides exactly two things, both about ACCOUNTS: whom an actor may administer
 * (`permitUserAdministration`, a strictly higher rank) and which role an actor may assign
 * (`permitRoleAssignment`, the same). It never decides what a role may DO, which is the grant
 * table's job alone. **An analyst sits between an operator and an administrator** so that an
 * administrator, who already reads everything an analyst reads, can open and close analyst
 * accounts the way they handle operator turnover, while only the lead administrator can make
 * somebody an administrator. The numbers are compared and never stored, so renumbering them is
 * safe; their ORDER is what `auth/__tests__/policy.test.ts` pins.
 */
export const ROLE_RANK = {
    operator: 1,
    analyst: 2,
    admin: 3,
    super_admin: 4,
};
export const userStatusSchema = z.enum(['active', 'disabled']);
/**
 * A password, bounded but not otherwise judged here.
 *
 * Strength is decided by the encryption library's own `validatePassword`, which accepts 20 or more
 * characters, or 8 or more with upper case, lower case, a digit and a symbol. Restating that rule
 * here would create a second opinion, and the two would drift.
 */
export const passwordSchema = z.string().min(8).max(512);
/**
 * A gate PIN: short by design, and protected by an attempt counter rather than by length.
 *
 * **Exactly six digits, which is the product constant AD-54 states and not a range.** This schema
 * admitted four to eight until Phase 27's endpoint matrix drove the route: the server stores a PIN
 * only at `crypto/password.ts`'s `PIN_LENGTH`, so a five-digit PIN passed the wire contract, was
 * refused by the service with `VALIDATION_INVALID_FORMAT` and carried NO field path — which means
 * the document told an integrator four digits were acceptable and the form had nothing to
 * highlight when they were not. Narrowed here for the reason `presence.require_override_reason`
 * was narrowed to `z.literal(true)`: somebody finds out when they type the number rather than
 * afterwards. `auth/__tests__/pin-contract.test.ts` ties this to the constant, in the package that
 * can see both.
 */
export const pinSchema = z
    .string()
    .regex(/^\d{6}$/u, { error: 'errors.validation.invalid_format' });
export const totpCodeSchema = z.string().regex(/^\d{6}$/u, { error: 'errors.auth.totp_invalid' });
/**
 * One of the ten single-use codes minted when an authenticator is enrolled.
 *
 * The alphabet is Crockford's without the four ambiguous glyphs, ten symbols per code, and a person
 * reads it off a printed sheet — so the wire form admits the separators and the case the sheet is
 * displayed in and the server canonicalises before comparing. It deliberately does **not** pin the
 * exact ten-symbol shape: the canonicaliser strips whitespace and hyphens, so a schema that
 * demanded exactly ten characters would refuse the very form the sheet shows, and pinning the
 * alphabet here would be a second opinion about a value only `auth/totp.ts` knows the shape of.
 * What it does pin is that the field cannot carry prose, a password or anything unbounded.
 */
export const recoveryCodeSchema = z
    .string()
    .trim()
    .min(10)
    .max(32)
    .regex(/^[\dA-Za-z][\dA-Za-z\s-]*$/u, { error: 'errors.validation.invalid_format' });
export const userSchema = strict({
    id: uuidSchema,
    email: emailSchema,
    displayName: z.string().trim().min(1).max(120),
    role: roleSchema,
    status: userStatusSchema,
    mustChangePassword: z.boolean(),
    totpEnrolledAt: isoDateTimeSchema.nullable(),
    hasPin: z.boolean(),
    lastLoginAt: isoDateTimeSchema.nullable(),
    lockedUntil: isoDateTimeSchema.nullable(),
    version: versionSchema,
    audit: auditStampSchema,
});
/**
 * Adding an account.
 *
 * **`password` is optional, and the omission is the ordinary case.** An administrator adding an
 * operator at a desk does not choose that person's password: the server mints a temporary one,
 * returns it exactly once in {@link createdUserSchema}, and stores only its Argon2id hash, so the
 * account arrives with `mustChangePassword` set and cannot reach any other route until the person
 * behind it has chosen their own. A caller that supplies one already knows it, which is why the
 * response withholds it in that case rather than echoing it into one more place.
 */
export const createUserRequestSchema = strict({
    email: emailSchema,
    displayName: z.string().trim().min(1).max(120),
    role: roleSchema.exclude(['super_admin']),
    password: passwordSchema.optional(),
});
/**
 * Amending a user.
 *
 * `super_admin` is excluded from the role a request may set, so the seeded account cannot be
 * cloned and a second one cannot be created through this route. Promotion to super administrator
 * is not a feature; there is exactly one, and it comes from the environment.
 */
export const updateUserRequestSchema = strict({
    displayName: z.string().trim().min(1).max(120).optional(),
    role: roleSchema.exclude(['super_admin']).optional(),
    status: userStatusSchema.optional(),
    version: versionSchema,
});
/**
 * Signing in.
 *
 * **`recoveryCode` is named here because the schema is strict and the recovery path is real.** The
 * one-time codes are minted at enrolment and the login service already spends one, but every schema
 * in this package refuses an unknown key, so a caller that submitted `recoveryCode` was refused at
 * the edge with `errors.validation.unknown_field` and the whole feature was unreachable over HTTP.
 *
 * **The two second factors are alternatives, never both.** The service gives the recovery code
 * precedence when both arrive, which is a precedence nobody chose; refusing the pair here means
 * there is nothing to have a precedence about, and a client that sent both learns it made a
 * mistake instead of having one of its two values silently ignored.
 */
export const loginRequestSchema = strict({
    email: emailSchema,
    password: passwordSchema,
    totpCode: totpCodeSchema.optional(),
    recoveryCode: recoveryCodeSchema.optional(),
}).refine((body) => body.totpCode === undefined || body.recoveryCode === undefined, {
    error: 'errors.validation.invalid_format',
    path: ['recoveryCode'],
});
export const changePasswordRequestSchema = strict({
    currentPassword: passwordSchema,
    newPassword: passwordSchema,
});
/** Re-authenticating at the gate after an idle lock: a PIN, never a full logout. */
export const pinUnlockRequestSchema = strict({ pin: pinSchema });
export const setPinRequestSchema = strict({
    currentPassword: passwordSchema,
    pin: pinSchema,
});
/**
 * The three states a session can be in that stop it reaching its ordinary routes.
 *
 * Declared here as well as in the server's route registry because it is part of the WIRE contract:
 * the session view reports which gate a session is behind so the client can render the right
 * dialogue, and a client that had to infer it from a 401 would be guessing between "sign in again",
 * "type your PIN", "choose a new password" and "enrol an authenticator" — four different screens.
 */
export const sessionGateSchema = z.enum(['idle_locked', 'password_change', 'totp_enrolment']);
/**
 * The authenticated session, as the client sees it. It carries no personal data beyond the user.
 *
 * **`idleExpiresAt` is the EARLIEST moment this session can lock, never the latest.** The idle
 * window belongs to the route rather than to the session (AD-54), so a session that has been
 * inactive for six minutes is fine on an admin screen and locked on a gate screen — and the lock is
 * session-wide once it trips. The only honest single instant to report is therefore
 * `lastSeenAt + session.idle_minutes_gate`, the soonest a lock can happen; reporting the admin
 * window instead would tell a gate terminal it had an hour when it had five minutes.
 *
 * **`gate` is null for a session that is behind nothing**, which is the ordinary case. It is
 * non-null exactly when this session would be refused on an ordinary route, and it names which of
 * the three ways out the client must offer.
 */
export const sessionSchema = strict({
    user: userSchema,
    issuedAt: isoDateTimeSchema,
    absoluteExpiresAt: isoDateTimeSchema,
    idleExpiresAt: isoDateTimeSchema,
    csrfToken: z.string().min(16).max(256),
    gate: sessionGateSchema.nullable(),
});
/**
 * What creating an account or resetting a password answers with.
 *
 * The temporary password is in this body and in no other place: nothing stores it, and no later
 * `GET` can produce it. `null` when the caller supplied the password itself, because echoing back a
 * value the caller already holds puts it into one more log, screenshot and browser history for no
 * benefit at all.
 */
export const createdUserSchema = strict({
    user: userSchema,
    temporaryPassword: z.string().min(8).max(512).nullable(),
});
/** A reset always mints, so the password is never null here. */
export const resetPasswordResponseSchema = strict({
    user: userSchema,
    temporaryPassword: z.string().min(8).max(512),
});
/** Clearing an account's authenticator enrolment and its recovery codes, and nothing else. */
export const resetTotpResponseSchema = strict({ user: userSchema });
/**
 * The first CSRF token, handed to a caller that has no session yet.
 *
 * It is its own route rather than a member of the client bootstrap because issuing a token SETS a
 * cookie, and a response that sets a credential cookie as a side effect of answering an unrelated
 * question is one nobody remembers is doing it. The token is bound to the shared anonymous
 * namespace until a session exists, which is what lets the login POST be protected by the same
 * mechanism as every other unsafe route rather than being the one exemption.
 */
export const csrfTokenSchema = strict({ csrfToken: z.string().min(16).max(256) });
/**
 * What a successful sign-in answers with.
 *
 * The session is the whole of what a client needs to proceed, including the fresh token bound to
 * the session id the login just rotated to. The two recovery members are present because spending
 * a one-time code is the moment to tell somebody how many they have left: a person who has burned
 * nine of ten and is never told has no warning before the tenth.
 */
export const loginResponseSchema = strict({
    session: sessionSchema,
    /** True when a recovery code was spent instead of an authenticator token. */
    usedRecoveryCode: z.boolean(),
    /** How many recovery codes remain, or null when none was spent. */
    recoveryCodesRemaining: z.int().nonnegative().nullable(),
});
/** Beginning an authenticator enrolment. Shown exactly once and never retrievable again. */
export const totpEnrolmentOfferSchema = strict({
    /** The base32 secret, for somebody who types it in rather than scanning. */
    secret: z.string().min(16).max(256),
    /** The `otpauth://` URI a QR code is rendered from. */
    uri: z.string().min(1).max(2048),
});
/** Proving the authenticator works. The token is the six digits it is showing right now. */
export const totpConfirmRequestSchema = strict({ token: totpCodeSchema });
/**
 * What confirming an enrolment answers with. The recovery codes appear here and in no other place.
 *
 * The session travels with them because confirming an enrolment clears the `totp_enrolment` gate
 * and rotates the session id, so the token the client was holding is bound to an identifier that no
 * longer exists. Without the fresh session in this body the very next write would be a 403.
 */
export const totpConfirmationSchema = strict({
    enrolledAt: isoDateTimeSchema,
    recoveryCodes: z.array(z.string().min(8).max(32)).min(1).max(20),
    session: sessionSchema,
});
/** Escalating from the PIN to the full password after an idle lock. */
export const passwordUnlockRequestSchema = strict({ password: passwordSchema });
/**
 * What a re-authentication at an idle-locked terminal answers with.
 *
 * `attemptsRemaining` is how many PIN attempts are left before the full password is demanded, which
 * is the number the modal counts down; it is reported on the success path too, because a correct
 * PIN re-opens the allowance and the screen has to stop showing a stale count.
 */
export const unlockResponseSchema = strict({
    session: sessionSchema,
    attemptsRemaining: z.int().nonnegative(),
});
/**
 * What signing out answers with.
 *
 * A body rather than a `204`, because every other route in this system answers with JSON through
 * one serialisation path (`http/validate.ts`), and a status with no body would be the one response
 * that leaves by a different door. There is nothing useful to report, so it reports that.
 */
export const loggedOutSchema = strict({ signedOut: z.literal(true) });
/**
 * Section 1.7's self-service reset: restoring your own password, or clearing your own authenticator,
 * against a one-time code e-mailed to the address the account is registered under.
 *
 * **Two purposes and one shape, because the difference is what the code UNLOCKS rather than how it
 * is obtained.** A `password` code lets the holder choose a new password; a `totp` code lets them
 * clear an authenticator enrolment, and requires the account's current password beside it. The
 * purpose is bound into the stored code's keyed hash as well as recorded on the row, so a code
 * minted for one can never be redeemed for the other even if both checks were forgotten.
 */
export const accountResetPurposeSchema = z.enum(['password', 'totp']);
/**
 * The code as it is typed back in.
 *
 * Deliberately loose about the grouping: it is shown as `XXXX-XXXX`, and somebody will type it
 * without the hyphen, in lower case, or with a space. The server canonicalises before it hashes,
 * so this only has to bound the length and refuse anything that is not a code at all. It is named
 * `code` and must stay named `code`, because `http/logging.ts`'s redaction list is closed and that
 * is the name on it.
 */
export const resetCodeSchema = z
    .string()
    .trim()
    .min(8)
    .max(20)
    .regex(/^[\dA-Za-z][\dA-Za-z\s-]*$/u, { error: 'errors.validation.invalid_format' });
/**
 * Asking for a code.
 *
 * `locale` is here and nowhere else in this product's mail, and the divergence is deliberate: every
 * other message goes to an operations list an administrator configured, while this one goes to one
 * person who is locked out and needs to be able to read it. It is validated to this product's two
 * languages, so nothing a caller submits can widen it.
 */
export const accountResetRequestSchema = strict({
    email: emailSchema,
    purpose: accountResetPurposeSchema,
    locale: localeSchema.optional(),
});
/**
 * What asking answers with, for every address alike.
 *
 * A literal rather than anything computed, because the whole security property of the request route
 * is that a known address, an unknown one and a disabled one are indistinguishable. There is no
 * member here that could differ between them.
 */
export const accountResetRequestedSchema = strict({ requested: z.literal(true) });
/** Redeeming a `password` code: the code, and the password to set. */
export const resetPasswordSubmitSchema = strict({
    code: resetCodeSchema,
    newPassword: passwordSchema,
});
/** Redeeming a `totp` code: the code, and the account's CURRENT password beside it. */
export const resetTotpSubmitSchema = strict({ code: resetCodeSchema, password: passwordSchema });
/**
 * What redeeming answers with, and what it deliberately does NOT carry.
 *
 * No session. Redeeming proves possession of a mailbox, and for the password flow that is one
 * factor; handing back a session would make e-mail alone enough to be signed in. The person signs
 * in afterwards, where the second factor is asked for as it always is.
 */
export const accountResetCompletedSchema = strict({ completed: z.literal(true) });
//# sourceMappingURL=user.js.map